import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import classNames from "classnames";
import { CSSProperties, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AutoSizer, Grid, List } from "react-virtualized";
import { BrowseDialogViewMode } from "../../app/slices/GlobalSlice";
import PiBrowseListRow from "../../components/PiBrowseListRow";
import { TranslatedPropDTO } from "../../dto/TranslatedPropDTO";
import useGridColumns from "../../hooks/useGridColumns";
import { PropCategoryChips, PropVersionTag } from "./PropCategoryChip";

/**
 * Tile geometry. Narrower than the mock's 200px floor would suggest is not
 * useful here — the preview image needs room — so the cells sit around
 * 210–260px depending on dialog width.
 */
export const PROP_TILE_MIN_WIDTH = 210;
export const PROP_TILE_GAP = 12;
export const PROP_TILE_ROW_HEIGHT = 192;
export const PROP_LIST_ROW_HEIGHT = 56;
export const DEFAULT_PROP_COLUMNS = 3;
/**
 * The Grid's vertical scrollbar lives INSIDE its width, so columns sized to
 * the full width overflow horizontally by exactly one scrollbar — a permanent
 * horizontal scrollbar under the tiles. Reserve its width up front.
 */
const VERTICAL_SCROLLBAR_ALLOWANCE = 12;

export const isUnloadedProp = (prop: TranslatedPropDTO | undefined) =>
  !prop || prop.id === "unloaded";

interface PropLibraryResultsProps {
  items: TranslatedPropDTO[];
  viewMode: BrowseDialogViewMode;
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  onConfirmIndex: (index: number) => void;
  /** Reports the measured column count so the arrow keys step by a row. */
  onColumnsChange: (columns: number) => void;
  /**
   * Ties the results to the search field for assistive tech: the field's
   * `aria-activedescendant` points at one of these option ids.
   */
  listboxId: string;
  optionIdFor: (index: number) => string;
  /** Drives page loading, in the pagination hook's (cols, rowStart, rowStop) contract. */
  onRangeRendered: (
    columns: number,
    rowStartIndex: number,
    rowStopIndex: number
  ) => void;
}

/**
 * The prop library's results region: a virtualised grid (tiles) or list
 * (rows) over the server-paged, sparse prop array. Width is measured — the
 * old `window.innerWidth * 0.9 - 878` derivation died with the fixed dialog.
 * The keyboard highlight is an index; scrollToRow pulls the virtual window
 * along when a key moves it off-screen.
 */
export default function PropLibraryResults(props: PropLibraryResultsProps) {
  const {
    items,
    viewMode,
    selectedIndex,
    onSelectIndex,
    onConfirmIndex,
    onColumnsChange,
    listboxId,
    optionIdFor,
    onRangeRendered,
  } = props;
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const measuredColumns = useGridColumns(containerRef, {
    minCellWidth: PROP_TILE_MIN_WIDTH,
    gap: PROP_TILE_GAP,
    fallback: DEFAULT_PROP_COLUMNS,
    max: 5,
  });
  const columns = viewMode === "list" ? 1 : measuredColumns;

  useEffect(() => {
    onColumnsChange(columns);
  }, [columns, onColumnsChange]);

  if (items.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-[24px] text-[13px] text-creator-text-sub text-center">
        {t("prop_library_ui.no_results")}
      </div>
    );
  }

  const skeleton = (
    <div className="w-full h-full rounded-[8px] bg-creator-childActive animate-pulse" />
  );

  const tileCell = (index: number, style: CSSProperties, key: string) => {
    const prop = items[index];
    if (index >= items.length) return <div key={key} style={style} />;

    return (
      <div key={key} style={style} className="p-[6px]">
        {isUnloadedProp(prop) ? (
          skeleton
        ) : (
          <button
            type="button"
            id={optionIdFor(index)}
            role="option"
            aria-selected={index === selectedIndex}
            tabIndex={-1}
            onClick={() => onSelectIndex(index)}
            onDoubleClick={() => onConfirmIndex(index)}
            className={classNames(
              // Hover is a border shift only: a background wash fights the
              // preview image's own fill, and the drop shadow read as noise.
              "w-full h-full rounded-[8px] border flex flex-col overflow-hidden text-left transition-colors",
              index === selectedIndex
                ? "border-creator-primary bg-creator-primary-washed"
                : "border-creator-border bg-creator-surface hover:border-[var(--pi-menu-text-color-1-faded)]"
            )}
          >
            <span className="h-[104px] w-full shrink-0 bg-interaction-softBackground flex items-center justify-center overflow-hidden">
              {prop.downloadUri ? (
                <img
                  src={prop.downloadUri}
                  alt=""
                  loading="lazy"
                  className="object-contain w-full h-full p-[10px]"
                />
              ) : (
                <span className="text-[40px] text-creator-text-faded flex">
                  <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
                </span>
              )}
            </span>
            <span className="flex-1 min-h-0 px-[10px] py-[8px] flex flex-col gap-[6px]">
              <span className="flex items-center gap-[8px]">
                <span className="flex-1 min-w-0 text-[13px] leading-[18px] text-creator-text truncate">
                  {prop.name}
                </span>
                <PropVersionTag version={prop.version} />
              </span>
              <span className="flex gap-[4px] overflow-hidden">
                <PropCategoryChips categoryIds={prop.categories} />
              </span>
            </span>
          </button>
        )}
      </div>
    );
  };

  const listRow = (index: number, style: CSSProperties, key: string) => {
    const prop = items[index];
    return (
      <div key={key} style={style} className="py-[2px]">
        {isUnloadedProp(prop) ? (
          <div className="mx-[8px] h-full rounded-[8px] bg-creator-childActive animate-pulse" />
        ) : (
          <PiBrowseListRow
            icon={
              prop.downloadUri ? (
                <img
                  src={prop.downloadUri}
                  alt=""
                  loading="lazy"
                  className="object-contain w-full h-full p-[2px]"
                />
              ) : (
                <ViewInArOutlinedIcon fontSize="inherit" color="inherit" />
              )
            }
            title={prop.name}
            subtitle={
              <span className="flex gap-[4px]">
                <PropCategoryChips categoryIds={prop.categories} />
              </span>
            }
            trailing={<PropVersionTag version={prop.version} />}
            optionId={optionIdFor(index)}
            isSelected={index === selectedIndex}
            onSelect={() => onSelectIndex(index)}
            onConfirm={() => onConfirmIndex(index)}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      id={listboxId}
      role="listbox"
      aria-label={t("prop_library_ui.results_label") ?? ""}
      className="flex-1 min-h-0 px-[10px] py-[8px]"
    >
      <AutoSizer>
        {({ width, height }) =>
          viewMode === "list" ? (
            <List
              width={width}
              height={height}
              rowCount={items.length}
              rowHeight={PROP_LIST_ROW_HEIGHT}
              scrollToIndex={selectedIndex}
              scrollToAlignment="auto"
              className="pit-quiet-scrollbar"
              rowRenderer={({ index, key, style }) =>
                listRow(index, style, key)
              }
              onRowsRendered={({ startIndex, stopIndex }) =>
                onRangeRendered(1, startIndex, stopIndex)
              }
            />
          ) : (
            <Grid
              width={width}
              height={height}
              columnCount={columns}
              columnWidth={Math.floor(
                (width - VERTICAL_SCROLLBAR_ALLOWANCE) / columns
              )}
              rowCount={Math.ceil(items.length / columns)}
              rowHeight={PROP_TILE_ROW_HEIGHT}
              scrollToRow={Math.floor(selectedIndex / columns)}
              scrollToColumn={selectedIndex % columns}
              scrollToAlignment="auto"
              className="pit-quiet-scrollbar"
              cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                tileCell(rowIndex * columns + columnIndex, style, key)
              }
              onSectionRendered={({ rowStartIndex, rowStopIndex }) =>
                onRangeRendered(columns, rowStartIndex, rowStopIndex)
              }
            />
          )
        }
      </AutoSizer>
    </div>
  );
}
